import { Helmet } from '@dr.pogodin/react-helmet';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { spacing } from 'src/constants/spacing.css';
import { useSelectedCompany } from 'src/contexts';

import { Api } from '../../api';
import { ApiError } from '../../api/hub-api-request';
import {
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Text,
} from '../../components';
import { Color } from '../../constants';
import {
  OverallStatusBanner,
  ServiceUptimeCard,
  UptimeLegend,
} from './components';
import { stateFromStatus } from './status';

export default function UptimePage() {
  const company = useSelectedCompany();
  const api = Api.CompanyUptime.useReadItemUptimeStatus(company.id);

  if (api.error) {
    if (api.error instanceof ApiError && api.error.response?.status === 404) {
      return (
        <PageWrap background={Color.neutral5} noPadding limitWidth="large">
          <Helmet>
            <title>{`Uptime | ${company.name}`}</title>
          </Helmet>
          <Stack padH={1.5} padV={1.5}>
            <EmptyState
              title="Uptime unavailable"
              subtitle="This company doesn't have a status page configured."
            />
          </Stack>
        </PageWrap>
      );
    }
    return <PageError {...api} />;
  }
  if (!api.data) return <PageLoading />;

  const resources = api.data.resources;
  const affectedCount = resources.filter(
    (resource) => stateFromStatus(resource.status) !== 'operational'
  ).length;

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`Uptime | ${company.name}`}</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <html.div>
          <Text typeface="body.medium" color="neutral100">
            Development
          </Text>
          <Text as="h1" typeface="headline" color="neutral100">
            Uptime
          </Text>
        </html.div>

        <OverallStatusBanner
          aggregateState={api.data.aggregateState}
          resourceCount={resources.length}
          affectedCount={affectedCount}
        />

        <html.div style={styles.listCard}>
          {resources.map((resource, index) => (
            <html.div
              key={resource['@id']}
              style={index > 0 ? styles.divider : undefined}
            >
              <ServiceUptimeCard resource={resource} />
            </html.div>
          ))}
        </html.div>

        <UptimeLegend />
      </Stack>
    </PageWrap>
  );
}

const styles = css.create({
  listCard: {
    backgroundColor: color.neutral0,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    paddingInline: `calc(${spacing.unit} * 1.5)`,
    paddingBlock: `calc(${spacing.unit} * 1.5)`,
    '@media (max-width: 768px)': {
      paddingInline: spacing.unit,
      paddingBlock: spacing.unit,
    },
  },
  divider: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: color.neutral20,
  },
});
